import type {
  AdminProvider,
  AttachmentUploadResult,
  CreateIncidentRequest,
  CreateIncidentResponse,
  ErrorResponse,
  Preferences,
  SessionResponse,
  TeamsMessageContext,
} from '../../api/src/shared/contract';
import { API_BASE_URL } from '../config';
import { acquireApiToken } from '../auth/msal';

/**
 * Typed client for the Function App. Every call carries the Entra Bearer token; Easy Auth
 * validates it before our code runs.
 */

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

async function authHeaders(extra?: HeadersInit): Promise<Headers> {
  const token = await acquireApiToken();
  const headers = new Headers(extra);
  headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function parseError(response: Response): Promise<ApiClientError> {
  let message = `Request failed (${response.status})`;
  let code: string | undefined;
  try {
    const body = (await response.json()) as ErrorResponse;
    if (typeof body.error === 'string' && body.error) message = body.error;
    if (typeof body.code === 'string') code = body.code;
  } catch {
    // Non-JSON error bodies become the generic message above.
  }
  return new ApiClientError(response.status, message, code);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders(init.headers);
  if (init.body && !(init.body instanceof ArrayBuffer) && !(init.body instanceof Uint8Array) && !(init.body instanceof Blob)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_URL}/api${path}`, { ...init, headers });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function getSession(providerId?: string | null): Promise<SessionResponse> {
  const params = providerId ? `?provider=${encodeURIComponent(providerId)}` : '';
  return request<SessionResponse>(`/session${params}`);
}

export function getMessageContext(id: string): Promise<TeamsMessageContext> {
  return request<TeamsMessageContext>(`/message-context/${encodeURIComponent(id)}`);
}

export function savePreferences(
  preferences: Preferences,
  providerId?: string | null,
): Promise<Preferences> {
  const body =
    providerId != null && providerId !== ''
      ? { ...preferences, providerId }
      : preferences;
  return request('/preferences', { method: 'PUT', body: JSON.stringify(body) });
}

export function createIncident(body: CreateIncidentRequest): Promise<CreateIncidentResponse> {
  return request('/incidents', { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Uploads one attachment as raw bytes. Filename travels in the query string so non-ASCII
 * names survive URL encoding; a header would mangle them.
 */
export async function uploadAttachment(
  incidentId: number,
  providerId: string,
  file: { name: string; contentType: string; bytes: ArrayBuffer },
): Promise<AttachmentUploadResult> {
  const params = new URLSearchParams({
    name: file.name,
    contentType: file.contentType || 'application/octet-stream',
    providerId,
  });
  return request(`/incidents/${incidentId}/attachments?${params}`, {
    method: 'POST',
    body: file.bytes,
    headers: { 'Content-Type': file.contentType || 'application/octet-stream' },
  });
}

/**
 * Admin API — browser MSAL uses the same Bearer path via acquireAdminApiToken.
 * Paths say "manage" because the Functions host reserves `api/admin/*` for itself and 404s it.
 */
export async function adminRequest<T>(
  path: string,
  init: RequestInit,
  acquireToken: () => Promise<string>,
): Promise<T> {
  const token = await acquireToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof ArrayBuffer) && !(init.body instanceof Uint8Array)) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(`${API_BASE_URL}/api${path}`, { ...init, headers });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function listAdminProviders(acquireToken: () => Promise<string>): Promise<{ providers: AdminProvider[] }> {
  return adminRequest('/manage/providers', { method: 'GET' }, acquireToken);
}

export function createAdminProvider(
  acquireToken: () => Promise<string>,
  body: { id: string; name: string },
): Promise<AdminProvider> {
  return adminRequest('/manage/providers', { method: 'POST', body: JSON.stringify(body) }, acquireToken);
}

export function renameAdminProvider(
  acquireToken: () => Promise<string>,
  id: string,
  name: string,
): Promise<AdminProvider> {
  return adminRequest(
    `/manage/providers/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify({ name }) },
    acquireToken,
  );
}

export function saveAdminProviderToken(
  acquireToken: () => Promise<string>,
  id: string,
  token: string,
): Promise<AdminProvider> {
  return adminRequest(
    `/manage/providers/${encodeURIComponent(id)}/token`,
    { method: 'PUT', body: JSON.stringify({ token }) },
    acquireToken,
  );
}

export function clearAdminProviderToken(
  acquireToken: () => Promise<string>,
  id: string,
): Promise<{ removed: boolean }> {
  return adminRequest(
    `/manage/providers/${encodeURIComponent(id)}/token`,
    { method: 'DELETE' },
    acquireToken,
  );
}

export function deleteAdminProvider(
  acquireToken: () => Promise<string>,
  id: string,
): Promise<{ removed: boolean }> {
  return adminRequest(
    `/manage/providers/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    acquireToken,
  );
}
